import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "afroam_cart_v1";
const CartContext = createContext(null);

const loadInitial = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(loadInitial);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* ignore quota errors */
    }
  }, [items]);

  const addItem = (product) => {
    setItems((current) => {
      const idx = current.findIndex((i) => i.product_id === product.id);
      if (idx >= 0) {
        const next = [...current];
        next[idx] = { ...next[idx], quantity: Math.min(next[idx].quantity + 1, 20) };
        return next;
      }
      return [
        ...current,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.image,
          quantity: 1,
        },
      ];
    });
    setIsOpen(true);
  };

  const removeItem = (product_id) =>
    setItems((current) => current.filter((i) => i.product_id !== product_id));

  const updateQuantity = (product_id, quantity) =>
    setItems((current) =>
      current
        .map((i) =>
          i.product_id === product_id
            ? { ...i, quantity: Math.max(1, Math.min(20, quantity)) }
            : i
        )
        .filter((i) => i.quantity > 0)
    );

  const clear = () => setItems([]);

  const open = () => setIsOpen(true);
  const close = () => setIsOpen(false);

  const value = useMemo(() => {
    const totalItems = items.reduce((sum, i) => sum + i.quantity, 0);
    const subtotal = items.reduce((sum, i) => sum + i.price * i.quantity, 0);
    return {
      items,
      isOpen,
      open,
      close,
      addItem,
      removeItem,
      updateQuantity,
      clear,
      totalItems,
      subtotal,
    };
  }, [items, isOpen]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
};
